import { useMemo, useCallback } from "react";
import {
  type AuditSession,
  type SourceRow,
} from "../models/audit/auditSession";
import { Citation } from "../models/audit/citation";
import {
  type AttributeTestColumnResult,
  type AuditorTrail,
} from "../models/audit/attributeTestColumn";
import { LinkingData } from "../pages/extraction/viewExtraction/PdfViewer";
import { type ViewerBoundingBox } from "../pages/extraction/viewExtraction/FileViewer";
import { citationsToViewerBoxes } from "../pages/audit/auditWorkPaperViewV2/lib/viewerBoxes";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { useUpdateAttributeTestResult } from "./useUpdateAttributeTestResult";
import { useAudit } from "../contexts/auditContext";
import { useHookCache } from "../lib/hookCache";
import { trailCacheKeys } from "./trailCacheKeys";
import { type BoundingBoxState } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";

function mapCitationsInGroups<
  T extends { citationGroups?: Array<{ citations: Citation[] }> },
>(step: T, fn: (citations: Citation[]) => Citation[]): T {
  if (!step.citationGroups || step.citationGroups.length === 0) return step;
  return {
    ...step,
    citationGroups: step.citationGroups.map((group) => ({
      ...group,
      citations: fn(group.citations),
    })),
  };
}

interface UseCitationActionsProps {
  auditSession: AuditSession;
  sourceRowId?: string;
  checkResultId?: string;
  attributeTestResult:
    | AuditSession["sourceRows"][number]["attributeTestResults"][number]
    | undefined;
  allCitations: Citation[];
  filteredCitations: Citation[];
  activeEvidenceIndex: number;
  currentBoundingBox: BoundingBoxState | undefined;
  setCurrentBoundingBox: (box: BoundingBoxState | undefined) => void;
  setActiveEvidenceIndex: (idx: number | ((prev: number) => number)) => void;
}

export function useCitationActions({
  auditSession,
  sourceRowId,
  checkResultId,
  attributeTestResult,
  allCitations,
  filteredCitations,
  activeEvidenceIndex,
  currentBoundingBox,
  setCurrentBoundingBox,
  setActiveEvidenceIndex,
}: UseCitationActionsProps) {
  const { currentSelectedDocumentUrl, selectedDocument } = useAudit();
  const { setEntry, invalidate } = useHookCache();
  const { updateResult } = useUpdateAttributeTestResult();

  // Auditor-trail citations (parent = an L2 step) live in a different cache
  // shape than the reperformance/inline optimistic writers touch, so after a
  // successful mutation on one we refetch the attribute-test detail (and its
  // source row) to reflect the change rather than threading the trail shape
  // through every writer.
  const invalidateTrailCaches = useCallback(
    (citation: Citation) => {
      if (!citation.auditorStepId || !attributeTestResult) return;
      trailCacheKeys(
        auditSession.id,
        attributeTestResult.id ?? "",
        sourceRowId,
      ).forEach((key) => void invalidate(key));
    },
    [invalidate, attributeTestResult, auditSession.id, sourceRowId],
  );

  const handleLinkingComplete = useCallback(
    async (linkingData: LinkingData) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error(
          "Missing source row, check result, or attribute test result information",
        );
        return;
      }

      const targetCitationId = currentBoundingBox?.citationId;
      const citation = allCitations.find((c) => c.id === targetCitationId);
      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      const pages = currentSelectedDocumentUrl?.documentExtraction?.pages;
      const pageMeta = pages?.[linkingData.pageNumber - 1];
      // The stored box and its page dimensions MUST share a coordinate space, or
      // `scaleX = displayedWidth / pageWidth` mis-places the box on re-render. A
      // freshly snipped box carries the exact dims it was drawn in
      // (`linkingData.pageWidth`); a dragged existing box doesn't, so fall back to
      // the citation's current dims (the space that box was already drawn in).
      const pageWidth =
        linkingData.pageWidth ??
        citation.pageWidth ??
        currentBoundingBox?.pageWidth ??
        pageMeta?.width ??
        pages?.[0]?.width;
      const pageHeight =
        linkingData.pageHeight ??
        citation.pageHeight ??
        currentBoundingBox?.pageHeight ??
        pageMeta?.height ??
        pages?.[0]?.height;

      // Apply the new box placement to the linked citation. Reused below to also
      // update the auditor-trail copy — the doc viewer reads its boxes from
      // `auditorTrail.steps[]`, so without this a trail citation snaps back to its
      // old position until the refetch lands.
      const applyBox = (c: Citation): Citation =>
        c.id === citation.id
          ? {
              ...c,
              boundingBox: linkingData.boundingBox,
              pageNumber: linkingData.pageNumber,
              pageWidth: pageWidth ?? null,
              pageHeight: pageHeight ?? null,
            }
          : c;
      const applyTrail = (
        trail: AuditorTrail | null | undefined,
      ): AuditorTrail | null | undefined =>
        trail
          ? {
              ...trail,
              steps: trail.steps.map((s) => ({
                ...s,
                citations: s.citations.map(applyBox),
              })),
            }
          : trail;

      // Phase 3: per-row optimistic write — the heavy AuditSession mirror
      // is gone; consumers read the row via `useSourceRow`.
      setEntry<SourceRow>(
        ["procedure-session", auditSession.id, "source-rows", sourceRowId],
        (prev) => ({
          ...prev,
          attributeTestResults: prev.attributeTestResults?.map((attrResult) =>
            attrResult.id === attributeTestResult.id
              ? {
                  ...attrResult,
                  inlineCitations: attrResult.inlineCitations.map((c) =>
                    c.id === citation.id
                      ? {
                          ...c,
                          boundingBox: linkingData.boundingBox,
                          pageNumber: linkingData.pageNumber,
                          pageWidth: pageWidth ?? null,
                          pageHeight: pageHeight ?? null,
                        }
                      : c,
                  ),
                  reperformanceSteps: attrResult.reperformanceSteps.map(
                    (step) => {
                      const mapCitation = (c: Citation) =>
                        c.id === citation.id
                          ? {
                              ...c,
                              boundingBox: linkingData.boundingBox,
                              pageNumber: linkingData.pageNumber,
                              pageWidth: pageWidth ?? null,
                              pageHeight: pageHeight ?? null,
                            }
                          : c;
                      return mapCitationsInGroups(
                        {
                          ...step,
                          citations: step.citations.map(mapCitation),
                        },
                        (citations) => citations.map(mapCitation),
                      );
                    },
                  ),
                  auditorTrail: applyTrail(attrResult.auditorTrail),
                }
              : attrResult,
          ),
        }),
      );
      // Workpaper-lazy-load: symmetric write to the per-attribute-test
      // detail cache (`useAttributeTestResultDetail`'s entry) so the side
      // panel sees the optimistic edit even though it no longer reads
      // through the heavy source-row tree.
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          inlineCitations: prev.inlineCitations.map((c) =>
            c.id === citation.id
              ? {
                  ...c,
                  boundingBox: linkingData.boundingBox,
                  pageNumber: linkingData.pageNumber,
                  pageWidth: pageWidth ?? null,
                  pageHeight: pageHeight ?? null,
                }
              : c,
          ),
          reperformanceSteps: prev.reperformanceSteps.map((step) => {
            const mapCitation = (c: Citation) =>
              c.id === citation.id
                ? {
                    ...c,
                    boundingBox: linkingData.boundingBox,
                    pageNumber: linkingData.pageNumber,
                    pageWidth: pageWidth ?? null,
                    pageHeight: pageHeight ?? null,
                  }
                : c;
            return mapCitationsInGroups(
              {
                ...step,
                citations: step.citations.map(mapCitation),
              },
              (citations) => citations.map(mapCitation),
            );
          }),
          auditorTrail: applyTrail(prev.auditorTrail),
        }),
      );

      setCurrentBoundingBox({
        boundingBox: linkingData.boundingBox,
        pageNumber: linkingData.pageNumber,
        pageWidth: pageWidth,
        pageHeight: pageHeight,
        elementId: "",
        citationId: citation.id,
      });

      try {
        await fetchFastApi({
          method: "PUT",
          path: `/citations/${citation.id}`,
          body: {
            citation: {
              ...citation,
              boundingBox: linkingData.boundingBox,
              pageNumber: linkingData.pageNumber,
              pageWidth: pageWidth ?? null,
              pageHeight: pageHeight ?? null,
            },
          },
        });
        invalidateTrailCaches(citation);
      } catch (error) {
        console.error("Error linking citation:", error);
        toast.error("Failed to link citation");
      }
    },
    [
      checkResultId,
      currentBoundingBox,
      auditSession.id,
      sourceRowId,
      currentSelectedDocumentUrl,
      setEntry,
      attributeTestResult,
      allCitations,
      setCurrentBoundingBox,
      invalidateTrailCaches,
    ],
  );

  const handleDeleteCitation = useCallback(
    async ({ id, index }: { id?: string; index: number }) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error(
          "Missing source row, check result, or attribute test result information",
        );
        return;
      }

      const targetCitationId = id || allCitations[index]?.id;
      const citation = allCitations.find((c) => c.id === targetCitationId);

      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      setEntry<SourceRow>(
        ["procedure-session", auditSession.id, "source-rows", sourceRowId],
        (prev) => ({
          ...prev,
          attributeTestResults: prev.attributeTestResults?.map((attrResult) =>
            attrResult.id === attributeTestResult.id
              ? {
                  ...attrResult,
                  inlineCitations: attrResult.inlineCitations.filter(
                    (c) => c.id !== citation.id,
                  ),
                  reperformanceSteps: attrResult.reperformanceSteps.map(
                    (step) =>
                      mapCitationsInGroups(
                        {
                          ...step,
                          citations: step.citations.filter(
                            (c) => c.id !== citation.id,
                          ),
                        },
                        (citations) =>
                          citations.filter((c) => c.id !== citation.id),
                      ),
                  ),
                }
              : attrResult,
          ),
        }),
      );
      // Workpaper-lazy-load: symmetric write to the per-attribute-test
      // detail cache.
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          inlineCitations: prev.inlineCitations.filter(
            (c) => c.id !== citation.id,
          ),
          reperformanceSteps: prev.reperformanceSteps.map((step) =>
            mapCitationsInGroups(
              {
                ...step,
                citations: step.citations.filter((c) => c.id !== citation.id),
              },
              (citations) => citations.filter((c) => c.id !== citation.id),
            ),
          ),
        }),
      );

      const remainingCitations = allCitations.filter(
        (c) => c.id !== citation.id,
      );
      if (remainingCitations.length > 0) {
        // Keep the selection on the same auditor-trail step (L2) so the
        // walkthrough stays put; fall back to the neighbouring citation.
        const sameStep = citation.auditorStepId
          ? remainingCitations.filter(
              (c) => c.auditorStepId === citation.auditorStepId,
            )
          : [];
        const newCitation =
          sameStep[0] ?? remainingCitations[Math.max(0, index - 1)];
        if (newCitation) {
          setCurrentBoundingBox({
            boundingBox: newCitation.boundingBox || undefined,
            pageNumber: newCitation.pageNumber || undefined,
            pageWidth: newCitation.pageWidth || undefined,
            pageHeight: newCitation.pageHeight || undefined,
            elementId: newCitation.encodedCitationString || undefined,
            citationId: newCitation.id,
          });

          const citIdx = filteredCitations.findIndex(
            (c) => c.id === newCitation.id,
          );
          if (citIdx >= 0) {
            setActiveEvidenceIndex(citIdx);
          }
        }
      } else {
        setCurrentBoundingBox(undefined);
        setActiveEvidenceIndex(0);
      }

      try {
        await fetchFastApi({
          method: "DELETE",
          path: `/citations/${citation.id}`,
        });
        // Auditor-trail citations live under `auditorTrail.steps[]`, which the
        // optimistic writes above don't touch — refetch so a deleted trail
        // citation drops out of the steps walkthrough instead of orphaning.
        invalidateTrailCaches(citation);
        toast.success("Citation deleted");
      } catch (error) {
        console.error("Error deleting citation:", error);
        toast.error("Failed to delete citation");
      }
    },
    [
      auditSession.id,
      sourceRowId,
      checkResultId,
      attributeTestResult,
      allCitations,
      filteredCitations,
      setEntry,
      setCurrentBoundingBox,
      setActiveEvidenceIndex,
      invalidateTrailCaches,
    ],
  );

  // Nulls the citation's box placement — boundingBox plus pageNumber/
  // pageWidth/pageHeight — in an in-place PUT. The page fields belong to a
  // specific page; a re-added box re-derives all of them from the document
  // extraction, so they are cleared together rather than left stale.
  // Unlike handleDeleteCitation, the citation itself stays in the walkthrough
  // / key evidence — only the document highlight is cleared.
  const handleRemoveBoundingBox = useCallback(
    async ({ id, index }: { id?: string; index: number }) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error(
          "Missing source row, check result, or attribute test result information",
        );
        return;
      }

      const targetCitationId = id || allCitations[index]?.id;
      const citation = allCitations.find((c) => c.id === targetCitationId);

      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      const clearBox = (c: Citation): Citation =>
        c.id === citation.id
          ? {
              ...c,
              boundingBox: null,
              pageNumber: null,
              pageWidth: null,
              pageHeight: null,
            }
          : c;

      setEntry<SourceRow>(
        ["procedure-session", auditSession.id, "source-rows", sourceRowId],
        (prev) => ({
          ...prev,
          attributeTestResults: prev.attributeTestResults?.map((attrResult) =>
            attrResult.id === attributeTestResult.id
              ? {
                  ...attrResult,
                  inlineCitations: attrResult.inlineCitations.map(clearBox),
                  reperformanceSteps: attrResult.reperformanceSteps.map(
                    (step) =>
                      mapCitationsInGroups(
                        {
                          ...step,
                          citations: step.citations.map(clearBox),
                        },
                        (citations) => citations.map(clearBox),
                      ),
                  ),
                }
              : attrResult,
          ),
        }),
      );
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          inlineCitations: prev.inlineCitations.map(clearBox),
          reperformanceSteps: prev.reperformanceSteps.map((step) =>
            mapCitationsInGroups(
              {
                ...step,
                citations: step.citations.map(clearBox),
              },
              (citations) => citations.map(clearBox),
            ),
          ),
        }),
      );

      // If the removed box was the active highlight, clear it. The trash icon
      // can target any box on the page, so leave other selections intact.
      if (currentBoundingBox?.citationId === citation.id) {
        setCurrentBoundingBox(undefined);
      }

      try {
        await fetchFastApi({
          method: "PUT",
          path: `/citations/${citation.id}`,
          body: {
            citation: {
              ...citation,
              boundingBox: null,
              pageNumber: null,
              pageWidth: null,
              pageHeight: null,
            },
          },
        });
        invalidateTrailCaches(citation);
        toast.success("Bounding box removed");
      } catch (error) {
        console.error("Error removing bounding box:", error);
        toast.error("Failed to remove bounding box");
      }
    },
    [
      auditSession.id,
      sourceRowId,
      checkResultId,
      attributeTestResult,
      allCitations,
      currentBoundingBox,
      setEntry,
      setCurrentBoundingBox,
      invalidateTrailCaches,
    ],
  );

  const handleUpdateCitationSummary = useCallback(
    async (citationId: string, newSummary: string) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error("Missing required information");
        return;
      }

      const citation = allCitations.find((c) => c.id === citationId);
      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      // Phase 3: per-row optimistic write.
      setEntry<SourceRow>(
        ["procedure-session", auditSession.id, "source-rows", sourceRowId],
        (prev) => ({
          ...prev,
          attributeTestResults: prev.attributeTestResults?.map((attrResult) =>
            attrResult.id === attributeTestResult.id
              ? {
                  ...attrResult,
                  inlineCitations: attrResult.inlineCitations.map((c) =>
                    c.id === citation.id
                      ? { ...c, shortSummary: newSummary }
                      : c,
                  ),
                  reperformanceSteps: attrResult.reperformanceSteps.map(
                    (step) => {
                      const mapCitation = (c: Citation) =>
                        c.id === citation.id
                          ? { ...c, shortSummary: newSummary }
                          : c;
                      return mapCitationsInGroups(
                        {
                          ...step,
                          citations: step.citations.map(mapCitation),
                        },
                        (citations) => citations.map(mapCitation),
                      );
                    },
                  ),
                }
              : attrResult,
          ),
        }),
      );
      // Workpaper-lazy-load: symmetric write to the per-attribute-test
      // detail cache.
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          inlineCitations: prev.inlineCitations.map((c) =>
            c.id === citation.id ? { ...c, shortSummary: newSummary } : c,
          ),
          reperformanceSteps: prev.reperformanceSteps.map((step) => {
            const mapCitation = (c: Citation) =>
              c.id === citation.id ? { ...c, shortSummary: newSummary } : c;
            return mapCitationsInGroups(
              {
                ...step,
                citations: step.citations.map(mapCitation),
              },
              (citations) => citations.map(mapCitation),
            );
          }),
        }),
      );

      try {
        await fetchFastApi({
          method: "PUT",
          path: `/citations/${citation.id}`,
          body: {
            citation: { ...citation, shortSummary: newSummary },
          },
        });

        // Trail citations live in a cache the optimistic writes above don't touch
        // (auditorTrail.steps[].citations) — refetch so the edit reflects.
        invalidateTrailCaches(citation);
        toast.success("Citation summary updated");
      } catch (error) {
        console.error("Error updating citation summary:", error);
        toast.error("Failed to update citation summary");
      }
    },
    [
      checkResultId,
      auditSession.id,
      sourceRowId,
      setEntry,
      attributeTestResult,
      allCitations,
      invalidateTrailCaches,
    ],
  );

  const handleToggleKeyCitation = useCallback(async () => {
    if (!sourceRowId || !checkResultId || !attributeTestResult) {
      toast.error("Missing required information");
      return;
    }

    const targetCitationId = currentBoundingBox?.citationId;
    const citation = allCitations.find((c) => c.id === targetCitationId);

    if (!citation) {
      toast.error("No citation selected");
      return;
    }

    const newIsKeyValue = !citation.isKey;

    // Phase 3: per-row optimistic write.
    setEntry<SourceRow>(
      ["procedure-session", auditSession.id, "source-rows", sourceRowId],
      (prev) => ({
        ...prev,
        attributeTestResults: prev.attributeTestResults?.map((attrResult) =>
          attrResult.id === attributeTestResult.id
            ? {
                ...attrResult,
                inlineCitations: attrResult.inlineCitations.map((c) =>
                  c.id === citation.id ? { ...c, isKey: newIsKeyValue } : c,
                ),
                reperformanceSteps: attrResult.reperformanceSteps.map(
                  (step) => {
                    const mapCitation = (c: Citation) =>
                      c.id === citation.id ? { ...c, isKey: newIsKeyValue } : c;
                    return mapCitationsInGroups(
                      {
                        ...step,
                        citations: step.citations.map(mapCitation),
                      },
                      (citations) => citations.map(mapCitation),
                    );
                  },
                ),
              }
            : attrResult,
        ),
      }),
    );
    // Workpaper-lazy-load: symmetric write to the per-attribute-test detail
    // cache.
    setEntry<AttributeTestColumnResult>(
      [
        "procedure-session",
        auditSession.id,
        "attribute-test-result",
        attributeTestResult.id ?? "",
      ],
      (prev) => ({
        ...prev,
        inlineCitations: prev.inlineCitations.map((c) =>
          c.id === citation.id ? { ...c, isKey: newIsKeyValue } : c,
        ),
        reperformanceSteps: prev.reperformanceSteps.map((step) => {
          const mapCitation = (c: Citation) =>
            c.id === citation.id ? { ...c, isKey: newIsKeyValue } : c;
          return mapCitationsInGroups(
            {
              ...step,
              citations: step.citations.map(mapCitation),
            },
            (citations) => citations.map(mapCitation),
          );
        }),
      }),
    );

    try {
      await fetchFastApi({
        method: "PUT",
        path: `/citations/${citation.id}`,
        body: {
          citation: { ...citation, isKey: newIsKeyValue },
        },
      });

      invalidateTrailCaches(citation);
      toast.success(
        newIsKeyValue
          ? "Citation will be included in workpaper"
          : "Citation removed from workpaper",
      );
    } catch (error) {
      console.error("Error toggling key citation:", error);
      toast.error("Failed to update citation");
    }
  }, [
    checkResultId,
    currentBoundingBox,
    auditSession.id,
    sourceRowId,
    setEntry,
    attributeTestResult,
    allCitations,
    invalidateTrailCaches,
  ]);

  // Toggle a specific citation's `is_key` (the "In Workpaper" column) — unlike
  // handleToggleKeyCitation it targets a passed citation, not the active one.
  // Persists via PUT; trail citations refresh through the cache invalidation.
  const handleToggleKeyForCitation = useCallback(
    async (citation: Citation) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error("Missing required information");
        return;
      }
      const newIsKeyValue = !citation.isKey;
      try {
        await fetchFastApi({
          method: "PUT",
          path: `/citations/${citation.id}`,
          body: { citation: { ...citation, isKey: newIsKeyValue } },
        });
        invalidateTrailCaches(citation);
        toast.success(
          newIsKeyValue
            ? "Citation will be included in workpaper"
            : "Citation removed from workpaper",
        );
      } catch (error) {
        console.error("Error toggling key citation:", error);
        toast.error("Failed to update citation");
      }
    },
    [sourceRowId, checkResultId, attributeTestResult, invalidateTrailCaches],
  );

  const handleCreateCitation = useCallback(
    async (params: {
      documentId: string;
      timestampSeconds?: number;
      shortSummary?: string;
      reperformanceStepId?: string;
      auditorStepId?: string;
    }) => {
      if (!sourceRowId || !checkResultId || !attributeTestResult) {
        toast.error(
          "Missing source row, check result, or attribute test result information",
        );
        return;
      }

      // Build encodedCitationString for video citation
      const encodedCitationString =
        params.timestampSeconds !== undefined
          ? `video#${params.documentId}#${params.timestampSeconds}`
          : params.documentId;

      try {
        // POST /citations API call
        const response = await fetchFastApi({
          method: "POST",
          path: "/citations",
          body: {
            citation: {
              documentId: params.documentId,
              encodedCitationString,
              shortSummary: params.shortSummary ?? null,
              timestampSeconds: params.timestampSeconds ?? null,
              // Parent precedence: an auditor-trail step (trail citations) wins
              // over a reperformance step, which wins over the attribute-test
              // result. Only one parent is sent.
              ...(params.auditorStepId
                ? { auditorStepId: params.auditorStepId }
                : params.reperformanceStepId
                  ? { reperformanceStepId: params.reperformanceStepId }
                  : { attributeTestResultId: attributeTestResult.id }),
              isKey: true, // Default to included in workpaper
            },
          },
        });

        const newCitation = response.citation;

        // Auditor-trail citations live under `auditorTrail.steps[].citations`,
        // a cache shape the inline optimistic writers below don't touch — so
        // refetch the trail caches to surface the new citation instead.
        if (params.auditorStepId) {
          invalidateTrailCaches(newCitation);
          setCurrentBoundingBox({
            timestampSeconds: params.timestampSeconds,
            elementId: encodedCitationString,
            citationId: newCitation.id,
          });
          toast.success("Citation created successfully");
          return;
        }

        // Phase 3: per-row optimistic write.
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => ({
            ...prev,
            attributeTestResults: prev.attributeTestResults?.map(
              (attrResult) =>
                attrResult.id === attributeTestResult.id
                  ? {
                      ...attrResult,
                      inlineCitations: [
                        ...attrResult.inlineCitations,
                        newCitation,
                      ],
                    }
                  : attrResult,
            ),
          }),
        );
        // Workpaper-lazy-load: symmetric write to the per-attribute-test
        // detail cache.
        setEntry<AttributeTestColumnResult>(
          [
            "procedure-session",
            auditSession.id,
            "attribute-test-result",
            attributeTestResult.id ?? "",
          ],
          (prev) => ({
            ...prev,
            inlineCitations: [...prev.inlineCitations, newCitation],
          }),
        );

        // Update the active evidence to show the new citation
        const newCitationIndex = filteredCitations.length;
        setActiveEvidenceIndex(newCitationIndex);

        // Set the current bounding box to navigate to the new citation
        setCurrentBoundingBox({
          timestampSeconds: params.timestampSeconds,
          elementId: encodedCitationString,
          citationId: newCitation.id,
        });

        toast.success("Citation created successfully");
      } catch (error) {
        console.error("Error creating citation:", error);
        toast.error("Failed to create citation");
      }
    },
    [
      sourceRowId,
      checkResultId,
      attributeTestResult,
      auditSession.id,
      setEntry,
      filteredCitations.length,
      setActiveEvidenceIndex,
      setCurrentBoundingBox,
      invalidateTrailCaches,
    ],
  );

  const handleUpdateField = useCallback(
    async (field: string, value: string | number | object | boolean | null) => {
      if (!attributeTestResult?.id) {
        toast.error("Cannot update: missing result ID");
        return;
      }

      const payload: Record<string, string | number | object | boolean | null> =
        {
          [field]: value,
        };

      // If updating outcome, automatically manage control_exceptions
      if (field === "outcome" && attributeTestResult.isConclusive) {
        if (value === "pass") {
          // Clear exceptions when changing to pass
          payload.control_exceptions = [];
        } else if (value === "fail") {
          // Ensure at least one exception exists when changing to fail
          const currentExceptions =
            "controlExceptions" in attributeTestResult
              ? (attributeTestResult.controlExceptions as string[])
              : [];
          if (currentExceptions.length === 0) {
            payload.control_exceptions = ["general_exception"];
          }
        }
        // not_applicable doesn't need exceptions
      } else if (field === "outcome" && !attributeTestResult.isConclusive) {
        // Transition from inconclusive to conclusive
        payload.is_conclusive = true;
        payload.performance_issues = [];
        payload.outcome_description = "";
        if (value === "fail") {
          payload.control_exceptions = ["general_exception"];
        } else {
          payload.control_exceptions = [];
        }
      }

      const success = await updateResult(attributeTestResult.id, payload);

      // Leaving inconclusive: the backend blanks the trail's L1 in the same
      // write (it was written for the inconclusive result), so mirror that here
      // and let the auditor write a conclusion for the new outcome.
      const conclusionCleared =
        success &&
        field === "outcome" &&
        !attributeTestResult.isConclusive &&
        attributeTestResult.auditorTrail != null;

      if (success && attributeTestResult) {
        // Build the camelCase field-update map once and reuse below for
        // both the per-row optimistic write and the per-attribute-test
        // detail cache write. The discriminated union is preserved by the
        // shallow `{ ...prev, ...updates }` spread — the result's runtime
        // `isConclusive` flag remains a literal boolean even when one of
        // the updates flips it.
        const updates: Record<string, unknown> = {};
        Object.keys(payload).forEach((key) => {
          if (key === "control_exceptions") {
            updates.controlExceptions = payload[key];
          } else if (key === "performance_issues") {
            updates.performanceIssues = payload[key];
          } else if (key === "outcome_description") {
            updates.outcomeDescription = payload[key];
          } else if (key === "partial_description") {
            updates.partialDescription = payload[key];
          } else if (key === "potential_mitigating_factors") {
            updates.potentialMitigatingFactors = payload[key];
          } else if (key === "evidence_summary") {
            updates.evidenceSummary = payload[key];
          } else if (key === "is_conclusive") {
            updates.isConclusive = payload[key];
          } else {
            updates[key] = payload[key];
          }
        });

        // Phase 3: per-row optimistic write. Only this cache is keyed by the
        // source row; a selection without one still updates the detail cache
        // and refetches below.
        if (sourceRowId) {
          setEntry<SourceRow>(
            ["procedure-session", auditSession.id, "source-rows", sourceRowId],
            (prev) => ({
              ...prev,
              attributeTestResults: prev.attributeTestResults?.map(
                (attrResult) => {
                  if (attrResult.id !== attributeTestResult.id) {
                    return attrResult;
                  }
                  return { ...attrResult, ...updates };
                },
              ),
            }),
          );
        }
        // Workpaper-lazy-load: symmetric write to the per-attribute-test
        // detail cache. The detail-shape uses the same camelCase fields
        // (and the same discriminated union) as the per-row entry, so the
        // same `updates` map applies.
        setEntry<AttributeTestColumnResult>(
          [
            "procedure-session",
            auditSession.id,
            "attribute-test-result",
            attributeTestResult.id ?? "",
          ],
          (prev) => ({
            ...prev,
            ...updates,
            // Blank the L1 alongside the outcome so the old inconclusive-era
            // conclusion never flashes before the refetch lands.
            ...(conclusionCleared && prev.auditorTrail
              ? { auditorTrail: { ...prev.auditorTrail, l1: "" } }
              : {}),
          }),
        );

        // The work-paper grid cell renders the outcome off the table-shell's
        // dry-row `attributeTestResultsSummary` (see
        // `WorkPaperTableV2.AttributeTestCell`), a separate cache from the two
        // above. Invalidate it so the cell refetches the persisted outcome
        // after an edit — otherwise the badge updates but the grid cell stays
        // stale.
        void invalidate(["procedure-session", auditSession.id, "table-shell"]);

        // Refetch the detail so the trail's now-blank L1 reaches the panel.
        if (conclusionCleared) {
          void invalidate([
            "procedure-session",
            auditSession.id,
            "attribute-test-result",
            attributeTestResult.id ?? "",
          ]);
        }
      }
    },
    [
      attributeTestResult,
      updateResult,
      sourceRowId,
      auditSession.id,
      setEntry,
      invalidate,
    ],
  );

  // Set an auditor-trail step's `is_exception` flag (the ✓ / ✗ status marker).
  // Refetches the trail caches so the icon reflects the change.
  const handleSetStepException = useCallback(
    async (stepId: string, isException: boolean) => {
      if (!attributeTestResult?.id) {
        toast.error("Cannot update: missing result ID");
        return;
      }
      try {
        await fetchFastApi({
          method: "PATCH",
          path: `/matching/attribute-test-results/${attributeTestResult.id}/auditor-steps/${stepId}`,
          body: { is_exception: isException },
        });
        void invalidate([
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id,
        ]);
        if (sourceRowId) {
          void invalidate([
            "procedure-session",
            auditSession.id,
            "source-rows",
            sourceRowId,
          ]);
        }
      } catch (error) {
        console.error("Error updating auditor step:", error);
        toast.error("Failed to update step status");
      }
    },
    [attributeTestResult, auditSession.id, sourceRowId, invalidate],
  );

  // Edit an auditor-trail step's text (the L2 summary). Refetches the trail caches
  // so the new text reflects.
  const handleSetStepText = useCallback(
    async (stepId: string, text: string) => {
      if (!attributeTestResult?.id) {
        toast.error("Cannot update: missing result ID");
        return;
      }
      try {
        await fetchFastApi({
          method: "PATCH",
          path: `/matching/attribute-test-results/${attributeTestResult.id}/auditor-steps/${stepId}`,
          body: { text },
        });
        void invalidate([
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id,
        ]);
        if (sourceRowId) {
          void invalidate([
            "procedure-session",
            auditSession.id,
            "source-rows",
            sourceRowId,
          ]);
        }
      } catch (error) {
        console.error("Error updating auditor step text:", error);
        toast.error("Failed to update step");
      }
    },
    [attributeTestResult, auditSession.id, sourceRowId, invalidate],
  );

  // Edit the auditor trail's L1 verdict (the test conclusion). Refetches the
  // trail caches so the new conclusion reflects.
  const handleSetTrailConclusion = useCallback(
    async (l1: string) => {
      if (!attributeTestResult?.id) {
        toast.error("Cannot update: missing result ID");
        return;
      }
      try {
        await fetchFastApi({
          method: "PATCH",
          path: `/matching/attribute-test-results/${attributeTestResult.id}/auditor-trail`,
          body: { l1 },
        });
        void invalidate([
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id,
        ]);
        if (sourceRowId) {
          void invalidate([
            "procedure-session",
            auditSession.id,
            "source-rows",
            sourceRowId,
          ]);
        }
      } catch (error) {
        console.error("Error updating auditor trail conclusion:", error);
        toast.error("Failed to update conclusion");
      }
    },
    [attributeTestResult, auditSession.id, sourceRowId, invalidate],
  );

  const currentDocId = useMemo(() => {
    if (selectedDocument?.documentId) {
      return selectedDocument.documentId;
    }
    const active = filteredCitations[activeEvidenceIndex];
    return active?.documentId;
  }, [selectedDocument?.documentId, filteredCitations, activeEvidenceIndex]);

  const viewerBoundingBoxes: ViewerBoundingBox[] | undefined = useMemo(
    () => citationsToViewerBoxes(allCitations, currentDocId),
    [allCitations, currentDocId],
  );

  const selectedBoundingBoxId: string | undefined = useMemo(() => {
    if (!currentBoundingBox?.citationId) return undefined;
    return currentBoundingBox.citationId;
  }, [currentBoundingBox?.citationId]);

  const selectedBoundingBoxIndex: number | undefined = useMemo(() => {
    if (!viewerBoundingBoxes || !currentBoundingBox?.citationId)
      return undefined;
    const idx = viewerBoundingBoxes.findIndex(
      (b) => b.id === currentBoundingBox.citationId,
    );
    return idx >= 0 ? idx : undefined;
  }, [viewerBoundingBoxes, currentBoundingBox?.citationId]);

  return {
    handleLinkingComplete,
    handleDeleteCitation,
    handleRemoveBoundingBox,
    handleToggleKeyCitation,
    handleToggleKeyForCitation,
    handleUpdateCitationSummary,
    handleCreateCitation,
    handleUpdateField,
    handleSetStepException,
    handleSetStepText,
    handleSetTrailConclusion,
    currentDocId,
    viewerBoundingBoxes,
    selectedBoundingBoxId,
    selectedBoundingBoxIndex,
  };
}
